import React, { useEffect, useMemo, useState } from 'react';
import { collection, deleteDoc, doc, getDoc, getDocs, onSnapshot, serverTimestamp, setDoc } from 'firebase/firestore';
import { signInWithEmailAndPassword, signOut, onAuthStateChanged } from 'firebase/auth';
import { db, auth as firebaseAuth } from '../firebase';
import { THEMES, applyTheme } from '../themes';
import './Admin.css';
const CLOUD = 'dsr3a9azn';
const PRESET = 'biscornue';

const slug = (s) => s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
const money = (n) => `€${Number(n).toFixed(2)}`;

// Jour de retrait réel : le champ `jour` est figé à la création (« Aujourd'hui »),
// on le recalcule depuis createdAt (+1 jour si la commande était pour le lendemain).
const jourRetrait = (s) => {
  if (!s.createdAt?.seconds) return null;
  const d = new Date(s.createdAt.seconds * 1000);
  const j = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  if (s.jour && s.jour !== "Aujourd'hui") j.setDate(j.getDate() + 1);
  return j;
};
const labelJour = (j) => {
  if (!j) return 'Date inconnue';
  const now = new Date();
  const diff = Math.round((j - new Date(now.getFullYear(), now.getMonth(), now.getDate())) / 86400000);
  if (diff === 0) return "Aujourd'hui";
  if (diff === 1) return 'Demain';
  if (diff === -1) return 'Hier';
  return j.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
};

// ── Statistiques : mêmes règles que la tablette ──
// Comptées : commandes acceptées / prêtes / livrées (pas « nouveau », pas « annulé »).
const STAT_PERIODES = [
  ['today', "Aujourd'hui"], ['hier', 'Hier'], ['semaine', 'Cette semaine'],
  ['semaine_der', 'Semaine dernière'], ['mois', 'Ce mois'], ['mois_der', 'Mois dernier'], ['tout', 'Tout'],
];
const STAT_MODES = [
  ['lig', 'Retrait Ligeria', '#AB3A1F'], ['tab', 'Retrait Tabac Oudon', '#283593'], ['sp', 'Sur place', '#E65100'],
];
const modeRetrait = (o) => (o.surPlace ? 'sp' : o.point === 'tab' ? 'tab' : 'lig');
function periodeBornes(k) {
  const now = new Date();
  const jour = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const lundi = new Date(jour); lundi.setDate(jour.getDate() - ((jour.getDay() + 6) % 7));
  const add = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
  switch (k) {
    case 'today': return [jour, add(jour, 1)];
    case 'hier': return [add(jour, -1), jour];
    case 'semaine': return [lundi, add(lundi, 7)];
    case 'semaine_der': return [add(lundi, -7), lundi];
    case 'mois': return [new Date(now.getFullYear(), now.getMonth(), 1), new Date(now.getFullYear(), now.getMonth() + 1, 1)];
    case 'mois_der': return [new Date(now.getFullYear(), now.getMonth() - 1, 1), new Date(now.getFullYear(), now.getMonth(), 1)];
    default: return [new Date(0), new Date(8640000000000000)];
  }
}
function calculStats(siparisler, periode) {
  const [debut, fin] = periodeBornes(periode);
  const res = { total: 0, commandes: 0, articles: 0, attente: 0, modes: { lig: { total: 0, commandes: 0 }, tab: { total: 0, commandes: 0 }, sp: { total: 0, commandes: 0 } } };
  siparisler.forEach(o => {
    const t = o.createdAt?.seconds ? new Date(o.createdAt.seconds * 1000) : null;
    if (!t || t < debut || t >= fin) return;
    if (o.statut === 'nouveau') { res.attente++; return; }
    if (o.statut === 'annulé') return;
    const m = res.modes[modeRetrait(o)];
    const tot = Number(o.total) || 0;
    res.total += tot; res.commandes++; m.total += tot; m.commandes++;
    (o.items || []).forEach(it => { res.articles += Number(it.quantite) || 1; });
  });
  return res;
}

function StatsSection({ siparisler }) {
  const [periode, setPeriode] = useState('today');
  const st = useMemo(() => calculStats(siparisler, periode), [siparisler, periode]);
  const [debut, fin] = periodeBornes(periode);
  const fmt = (d) => d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  const label = periode === 'tout' ? 'Toutes les commandes enregistrées'
    : periode === 'today' || periode === 'hier' ? debut.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })
    : `du ${fmt(debut)} au ${fmt(new Date(fin - 1))}`;
  const box = { background: '#fff', border: '1px solid #E5E7EB', borderRadius: 14, padding: 18 };
  return (
    <>
      <div className="admin-topbar"><div><h1>Statistiques</h1><p>Chiffre d'affaires et argent encaissé au Tabac Oudon</p></div></div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
        {STAT_PERIODES.map(([k, l]) => (
          <button key={k} onClick={() => setPeriode(k)} style={{
            padding: '8px 14px', borderRadius: 999, cursor: 'pointer', fontWeight: 700, fontSize: 13,
            border: periode === k ? '2px solid #AB3A1F' : '1px solid #D1D5DB',
            background: periode === k ? '#AB3A1F' : '#fff', color: periode === k ? '#fff' : '#374151' }}>{l}</button>
        ))}
      </div>
      <p style={{ color: '#6B7280', fontSize: 13, margin: '0 0 12px', textTransform: 'capitalize' }}>{label}</p>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12, marginBottom: 12 }}>
        <div style={{ ...box, borderColor: '#111' }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase' }}>Total des ventes</div>
          <div style={{ fontSize: 34, fontWeight: 900, margin: '4px 0' }}>{money(st.total)}</div>
          <div style={{ fontSize: 13, color: '#6B7280' }}>{st.commandes} commande{st.commandes !== 1 ? 's' : ''} · {st.articles} produit{st.articles !== 1 ? 's' : ''} vendu{st.articles !== 1 ? 's' : ''}</div>
        </div>
        <div style={{ ...box, background: '#EEF0FA', borderColor: '#283593', borderWidth: 2 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#283593', textTransform: 'uppercase' }}>💶 Encaissé au Tabac Oudon</div>
          <div style={{ fontSize: 34, fontWeight: 900, color: '#283593', margin: '4px 0' }}>{money(st.modes.tab.total)}</div>
          <div style={{ fontSize: 13, color: '#283593' }}>{st.modes.tab.commandes} commande{st.modes.tab.commandes !== 1 ? 's' : ''} retirée{st.modes.tab.commandes !== 1 ? 's' : ''} au Tabac Oudon</div>
        </div>
      </div>

      <div style={{ ...box, marginBottom: 12 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', marginBottom: 8 }}>Détail par lieu</div>
        {STAT_MODES.map(([k, l, c]) => (
          <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderTop: '1px solid #F3F4F6' }}>
            <span style={{ width: 10, height: 10, borderRadius: 3, background: c }} />
            <span style={{ flex: 1, fontWeight: 700 }}>{l}</span>
            <span style={{ color: '#6B7280', fontSize: 13 }}>{st.modes[k].commandes} cmd</span>
            <span style={{ fontWeight: 900, minWidth: 90, textAlign: 'right' }}>{money(st.modes[k].total)}</span>
          </div>
        ))}
      </div>
      <p style={{ color: '#6B7280', fontSize: 12 }}>
        Comptées : commandes acceptées, prêtes ou livrées. Les commandes annulées ne sont pas comptées
        {st.attente > 0 ? `, ni les ${st.attente} commande${st.attente > 1 ? 's' : ''} encore en attente` : ''}.
        Attention : « Supprimer les anciennes » dans Commandes efface aussi ces chiffres.
      </p>
    </>
  );
}

// Auto-format time input → HH:MM as user types
const fmtTime = (raw) => {
  const digits = raw.replace(/\D/g, '').slice(0, 4);
  if (digits.length <= 2) return digits;
  return digits.slice(0, 2) + ':' + digits.slice(2);
};

const TYPE_GROUP_IDS = ['type_sand', 'type_tacos', 'type_burger', 'choix_enfant', 'choix_pf'];
const CATEGORY_LABELS = {
  type_sand: 'Sandwiches',
  type_tacos: 'Tacos',
  type_burger: 'Burgers',
  choix_enfant: 'Menu Enfant',
  choix_pf: 'Petit Faim',
};

const BASE_PRODUCTS = [
  { id: 'fais-ta-pizza', title: 'FAIS TA PIZZA', category: 'Composez votre pizza', price: '11.00', displayPrice: '11,00 €', subtitle: '', notes: [],
    optionGroupIds: ['fp_base', 'fp_sauce', 'fp_legumes', 'fp_viandes', 'fp_fromages', 'fp_charcuterie'], maxIngredients: 6, ordre: 0,
    description: "Choisissez votre base, votre sauce (+1 €) puis jusqu'à 6 ingrédients : légumes +1 €, fromages +2 €, viandes et charcuterie +2,50 €.", image: '' },
  { id: 'saumon',        title: 'SAUMON',      category: 'Les Classiques',          price: '17.00', displayPrice: '17,00 €', subtitle: '', notes: [], optionGroupIds: [], ordre: 1,
    description: "Crème fraîche, mozzarella, saumon fumé « du Fumoir », artichaut, oignons rouges, aneth, citron.", image: '' },
  { id: 'cinq-fromages', title: '5 FROMAGES',  category: 'Les Classiques',          price: '16.00', displayPrice: '16,00 €', subtitle: '', notes: [], optionGroupIds: ['base'], ordre: 2,
    description: 'Base crème ou base tomate, mozzarella, chèvre, gorgonzola, parmesan, taleggio.', image: '/img/quatre-fromages.webp' },
  { id: 'calzone',       title: 'CALZONE',     category: 'Les Classiques',          price: '14.00', displayPrice: '14,00 €', subtitle: '', notes: [], optionGroupIds: [], ordre: 3,
    description: 'Sauce tomate, mozzarella, jambon, champignons, œuf.', image: '/img/calzone.webp' },
  { id: 'reine',         title: 'REINE',       category: 'Les Classiques',          price: '12.50', displayPrice: '12,50 €', subtitle: '', notes: [], optionGroupIds: [], ordre: 4,
    description: 'Sauce tomate, mozzarella, jambon, champignons.', image: '/img/reine.webp' },
  { id: 'marguerite',    title: 'MARGUERITE',  category: 'Les Classiques',          price: '11.00', displayPrice: '11,00 €', subtitle: '', notes: [], optionGroupIds: [], ordre: 5,
    description: "Sauce tomate, mozzarella, basilic, huile d'olive.", image: '/img/margherita.webp' },
  { id: 'majestueux',    title: 'MAJESTUEUX',  category: 'Les Spéciales Biscornue', price: '18.00', displayPrice: '18,00 €', subtitle: '', notes: [], optionGroupIds: [], ordre: 6,
    description: 'Sauce BBQ, viande hachée, jambon blanc, œuf, oignons caramélisés, cornichons, sauce cheddar.', image: '' },
  { id: 'biquette',      title: 'BIQUETTE',    category: 'Les Spéciales Biscornue', price: '15.00', displayPrice: '15,00 €', subtitle: '', notes: [], optionGroupIds: [], ordre: 7,
    description: 'Crème fraîche, fromage de chèvre, noix, miel, oignons caramélisés.', image: '/img/chevre-miel.webp' },
  { id: 'totoro',        title: 'TOTORO',      category: 'Les Spéciales Biscornue', price: '16.50', displayPrice: '16,50 €', subtitle: '', notes: [], optionGroupIds: [], ordre: 8,
    description: 'Sauce Totoro, tenders, pommes de terre, oignons rouges, sauce sweet chili.', image: '' },
  { id: 'curryosite',    title: 'CURRYOSITÉ',  category: 'Les Spéciales Biscornue', price: '16.00', displayPrice: '16,00 €', subtitle: '', notes: [], optionGroupIds: [], ordre: 9,
    description: 'Crème fraîche, poulet au curry, légumes grillés, feta.', image: '' },
];

const BASE_CATEGORIES = [
  { id: 'composez',   name: 'Composez votre pizza',    icon: '', ordre: 0 },
  { id: 'classiques', name: 'Les Classiques',          icon: '', ordre: 1 },
  { id: 'speciales',  name: 'Les Spéciales Biscornue', icon: '', ordre: 2 },
];

const BASE_OPTION_GROUPS = [
  // Seule option de la carte : la base du 5 Fromages
  { id: 'base', title: 'CHOISIS TA BASE', subtitle: 'Sélection obligatoire', min: 1, max: 1, columns: 2, items: [
    { id: 'creme',  label: 'Base crème',  price: 0, image: '' },
    { id: 'tomate', label: 'Base tomate', price: 0, image: '' },
  ]},
  // ── Fais ta pizza : base incluse + 6 ingrédients maximum au total ──
  { id: 'fp_base', title: 'CHOISIS TA BASE', subtitle: 'Incluse dans le prix · 1 choix obligatoire', min: 1, max: 1, columns: 2, items: [
    { id: 'creme_mozza',  label: 'Crème + mozzarella',  price: 0, image: '' },
    { id: 'tomate_mozza', label: 'Tomate + mozzarella', price: 0, image: '' },
  ]},
  // Sauce en plus de la base : ne compte pas dans les 6 ingrédients
  { id: 'fp_sauce', title: 'SAUCE', subtitle: '+1,00 € par sauce', min: 1, max: 2, columns: 2, items: [
    { id: 'sans_sauce',    label: 'Sans sauce',    price: 0, image: '' },
    { id: 'sauce_totoro',  label: 'Sauce Totoro',  price: 1, image: '' },
    { id: 'sauce_bbq',     label: 'Sauce barbecue', price: 1, image: '' },
  ]},
  { id: 'fp_legumes', title: 'LÉGUMES', subtitle: '+1,00 € par légume', min: 1, max: 8, columns: 2, ingredient: true, items: [
    { id: 'sans_legumes',    label: 'Sans légumes',    price: 0, image: '' },
    { id: 'champignons',     label: 'Champignons',     price: 1, image: '' },
    { id: 'poivrons',        label: 'Poivrons',        price: 1, image: '' },
    { id: 'tomates_cerises', label: 'Tomates cerises', price: 1, image: '' },
    { id: 'courgettes',      label: 'Courgettes',      price: 1, image: '' },
    { id: 'aubergines',      label: 'Aubergines',      price: 1, image: '' },
    { id: 'oignons_rouges',  label: 'Oignons rouges',  price: 1, image: '' },
    { id: 'oignons_confits', label: 'Oignons confits', price: 1, image: '' },
  ]},
  { id: 'fp_viandes', title: 'VIANDES', subtitle: '+2,50 € par viande', min: 1, max: 4, columns: 2, ingredient: true, items: [
    { id: 'sans_viande',     label: 'Sans viande',              price: 0,   image: '' },
    { id: 'effiloche_boeuf', label: 'Effiloché de bœuf confit', price: 2.5, image: '' },
    { id: 'poulet_curry',    label: 'Poulet curry',             price: 2.5, image: '' },
    { id: 'poulet_pane',     label: 'Poulet pané (tenders)',    price: 2.5, image: '' },
  ]},
  { id: 'fp_fromages', title: 'FROMAGES', subtitle: '+2,00 € par fromage', min: 1, max: 5, columns: 2, ingredient: true, items: [
    { id: 'sans_fromage', label: 'Sans fromage', price: 0, image: '' },
    { id: 'taleggio',     label: 'Taleggio',     price: 2, image: '' },
    { id: 'gorgonzola',   label: 'Gorgonzola',   price: 2, image: '' },
    { id: 'chevre',       label: 'Chèvre',       price: 2, image: '' },
    { id: 'parmigiano',   label: 'Parmigiano',   price: 2, image: '' },
  ]},
  { id: 'fp_charcuterie', title: 'CHARCUTERIE', subtitle: '+2,50 € par charcuterie', min: 1, max: 4, columns: 2, ingredient: true, items: [
    { id: 'sans_charcuterie', label: 'Sans charcuterie', price: 0,   image: '' },
    { id: 'jambon_blanc',     label: 'Jambon blanc',     price: 2.5, image: '' },
    { id: 'jambon_sec',       label: 'Jambon sec',       price: 2.5, image: '' },
    { id: 'spianata',         label: 'Spianata',         price: 2.5, image: '' },
  ]},
];

const emptyProduct = { id: '', title: '', subtitle: '', description: '', price: '€10.00', tag: 'Fait maison', category: '', image: '', notesText: '', optionGroupIds: [], actif: true };
const emptyGroup = { id: '', title: '', subtitle: '', min: 1, max: 1, columns: 2, items: [{ id: '', label: '', price: 0, image: '' }], actif: true };

function Admin() {
  const [auth, setAuth] = useState(false);
  const [pwInput, setPwInput] = useState('');
  const [emailInput, setEmailInput] = useState('La.Biscornue@gmail.com');
  const [loginLoading, setLoginLoading] = useState(false);
  const [section, setSection] = useState('produits');
  const [produits, setProduits] = useState({});
  const [categories, setCategories] = useState({});
  const [optionGroups, setOptionGroups] = useState({});
  const [siparisler, setSiparisler] = useState([]);
  const [images, setImages] = useState({});
  const prevNouveauCount = React.useRef(null);
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyProduct);
  const [editingGroup, setEditingGroup] = useState(null);
  const [groupForm, setGroupForm] = useState(emptyGroup);
  const [catForm, setCatForm] = useState({ name: '', icon: '🍽' });
  const [settings, setSettings] = useState({ delai: 15, ferme: false, services: [{ ouverture: '11:00', fermeture: '22:00', actif: true }] });
  const [editingItem, setEditingItem] = useState(null); // { groupId, itemIndex }
  const [itemForm, setItemForm] = useState({ label: '', price: 0, description: '', image: '', groupId: '' });

  // Firebase Auth state listener
  useEffect(() => {
    const unsub = onAuthStateChanged(firebaseAuth, user => setAuth(!!user));
    return unsub;
  }, []);

  useEffect(() => {
    if (!auth) return;
    const u1 = onSnapshot(collection(db, 'produits'), snap => { const d = {}; snap.forEach(s => { const v = s.data(); if (!v.deleted) d[s.id] = { id: s.id, ...v }; }); setProduits(d); });
    const u2 = onSnapshot(collection(db, 'categories'), snap => { const d = {}; snap.forEach(s => d[s.id] = { id: s.id, ...s.data() }); setCategories(d); });
    const u3 = onSnapshot(collection(db, 'optionGroups'), snap => { const d = {}; snap.forEach(s => d[s.id] = { id: s.id, ...s.data() }); setOptionGroups(d); });
    // Auto-créer les option groups manquants + forcer max correct
    (async () => {
      for (const g of BASE_OPTION_GROUPS) {
        const snap = await getDoc(doc(db, 'optionGroups', g.id));
        if (!snap.exists()) {
          await setDoc(doc(db, 'optionGroups', g.id), { ...g, actif: true, updatedAt: serverTimestamp() });
        } else {
          // Forcer la mise à jour du champ max si incorrect (ex: crudites_enfant max:2)
          // Et mettre à jour les items si les prix/labels ont changé (ex: nuggets 5→6, 7.90→6.90)
          const data = snap.data();
          const itemsChanged = (id) => JSON.stringify(data.items?.map(i=>({id:i.id,label:i.label,price:i.price}))) !== JSON.stringify(g.items?.map(i=>({id:i.id,label:i.label,price:i.price})));
          const needsUpdate = data.max !== g.max ||
            (['choix_enfant', 'sauce_tacos'].includes(g.id) && itemsChanged(g.id));
          if (needsUpdate) {
            const existingItems = data.items || [];
            const mergedItems = g.items.map(baseItem => {
              const existing = existingItems.find(it => it.id === baseItem.id);
              return { ...baseItem, image: existing?.image || baseItem.image || '' };
            });
            await setDoc(doc(db, 'optionGroups', g.id), { max: g.max, items: mergedItems, updatedAt: serverTimestamp() }, { merge: true });
          }
        }
      }
      // Auto-sync optionGroupIds des produits (ex: tacos sauce → sauce_tacos)
      for (const p of BASE_PRODUCTS) {
        if (!p.optionGroupIds) continue;
        const pSnap = await getDoc(doc(db, 'produits', p.id));
        if (!pSnap.exists()) continue;
        const pData = pSnap.data();
        if (JSON.stringify(pData.optionGroupIds) !== JSON.stringify(p.optionGroupIds)) {
          await setDoc(doc(db, 'produits', p.id), { optionGroupIds: p.optionGroupIds, updatedAt: serverTimestamp() }, { merge: true });
        }
      }
    })();
    const u4 = onSnapshot(collection(db, 'images'), snap => { const d = {}; snap.forEach(s => { if (s.data().image) d[s.id] = s.data().image; }); setImages(d); });
    const u6 = onSnapshot(doc(db, 'settings', 'general'), snap => { if (snap.exists()) setSettings(prev => ({ ...prev, ...snap.data() })); });
    const u5 = onSnapshot(collection(db, 'siparisler'), snap => {
      const list = [];
      snap.forEach(s => list.push({ id: s.id, ...s.data() }));
      list.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      setSiparisler(list);
      const nouveauCount = list.filter(s => s.statut === 'nouveau').length;
      if (prevNouveauCount.current !== null && nouveauCount > prevNouveauCount.current) {
        try { new Audio('/notif.mp3').play(); } catch (e) {}
      }
      prevNouveauCount.current = nouveauCount;
    });
    return () => { u1(); u2(); u3(); u4(); u5(); u6(); };
  }, [auth]);

  // Commandes groupées par jour de retrait : à venir d'abord, passées ensuite
  const joursCommandes = useMemo(() => {
    const map = new Map();
    siparisler.forEach(s => {
      const j = jourRetrait(s);
      const cle = j ? j.toISOString().slice(0, 10) : 'inconnu';
      if (!map.has(cle)) map.set(cle, []);
      map.get(cle).push({ ...s, jourDate: j });
    });
    const now = new Date();
    const auj = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    return [...map.entries()].sort((a, b) => {
      const ta = new Date(a[0]).getTime(), tb = new Date(b[0]).getTime();
      const fa = ta >= auj, fb = tb >= auj;
      if (fa !== fb) return fa ? -1 : 1;   // jours à venir (et aujourd'hui) en premier
      return fa ? ta - tb : tb - ta;        // à venir : du plus proche ; passés : du plus récent
    });
  }, [siparisler]);

  const produitsList = useMemo(() => Object.values(produits).filter(p => !p.deleted).sort((a, b) => (a.ordre ?? 999) - (b.ordre ?? 999)), [produits]);
  const catList = useMemo(() => Object.values(categories).sort((a, b) => (a.ordre ?? 999) - (b.ordre ?? 999)), [categories]);
  const groupList = useMemo(() => Object.values(optionGroups).sort((a, b) => (a.ordre ?? 999) - (b.ordre ?? 999)), [optionGroups]);

  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(''), 4000); };

  const flattenToJpeg = (file) => new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);
      URL.revokeObjectURL(url);
      canvas.toBlob(resolve, 'image/jpeg', 0.92);
    };
    img.src = url;
  });

  const upload = async (file) => {
    setBusy(true);
    try {
      const toUpload = file.type === 'image/png' ? await flattenToJpeg(file) : file;
      const fd = new FormData(); fd.append('file', toUpload); fd.append('upload_preset', PRESET);
      const r = await fetch(`https://api.cloudinary.com/v1_1/${CLOUD}/image/upload`, { method: 'POST', body: fd });
      const d = await r.json();
      if (!d.secure_url) throw new Error(d.error?.message || 'Upload failed');
      return d.secure_url;
    } finally { setBusy(false); }
  };

  /* SEED */
  const seed = async () => {
    if (!window.confirm(
      'Remplacer toute la carte par les pizzas du Ligeria ?\n\n'
      + 'Tout ce qui n\'est pas dans cette liste sera SUPPRIMÉ DÉFINITIVEMENT : '
      + 'anciens produits, options, catégories et photos. Les commandes ne sont pas touchées.'
    )) return;
    setBusy(true);
    try {
      const baseIds = BASE_PRODUCTS.map(p => p.id);
      const groupIds = BASE_OPTION_GROUPS.map(g => g.id);
      const catIds = BASE_CATEGORIES.map(c => c.id);
      let n = 0;
      // Suppression de tout ce qui n'appartient pas à la carte pizzas
      // Lecture directe : la liste de l'admin cache les produits déjà « supprimés » (deleted: true)
      const tousProduits = await getDocs(collection(db, 'produits'));
      for (const d of tousProduits.docs)           if (!baseIds.includes(d.id)) { await deleteDoc(d.ref);                        n++; }
      for (const id of Object.keys(optionGroups)) if (!groupIds.includes(id)) { await deleteDoc(doc(db, 'optionGroups', id)); n++; }
      for (const id of Object.keys(categories))   if (!catIds.includes(id))   { await deleteDoc(doc(db, 'categories', id));   n++; }
      for (const id of Object.keys(images))       if (!baseIds.includes(id))  { await deleteDoc(doc(db, 'images', id));       n++; }
      for (const p of BASE_PRODUCTS) {
        // Lire l'image existante et la conserver
        const existSnap = await getDoc(doc(db, 'produits', p.id));
        const existingImage = existSnap.exists() ? (existSnap.data().image || '') : '';
        const payload = { ...p, source: 'admin', actif: true, deleted: false, updatedAt: serverTimestamp() };
        payload.image = existingImage || p.image || '';
        await setDoc(doc(db, 'produits', p.id), payload, { merge: true });
      }
      for (const c of BASE_CATEGORIES) await setDoc(doc(db, 'categories', c.id), { ...c, actif: true, ordre: BASE_CATEGORIES.indexOf(c) + 1, updatedAt: serverTimestamp() }, { merge: true });
      for (const g of BASE_OPTION_GROUPS) {
        // Lire les items existants pour restaurer leurs images
        const existSnap = await getDoc(doc(db, 'optionGroups', g.id));
        const existingItems = existSnap.exists() ? (existSnap.data().items || []) : [];
        const mergedItems = g.items.map(baseItem => {
          const existing = existingItems.find(it => it.id === baseItem.id);
          // Garder l'image Firestore si elle existe, sinon garder celle de base
          return { ...baseItem, image: (existing?.image) || baseItem.image || '' };
        });
        await setDoc(doc(db, 'optionGroups', g.id), { ...g, items: mergedItems, actif: true, updatedAt: serverTimestamp() }, { merge: true });
      }
      flash(`✅ Carte pizzas en place — ${n} ancien(s) élément(s) supprimé(s).`);
    } catch (e) { alert('Erreur: ' + e.message); }
    finally { setBusy(false); }
  };

  /* PRODUIT SAVE */
  const saveProduit = async () => {
    if (!form.title.trim()) return flash('Le nom est obligatoire.');
    setBusy(true);
    try {
      const id = form.id || slug(form.title) || `p-${Date.now()}`;
      const notes = form.notesText.split('\n').map(s => s.trim()).filter(Boolean).slice(0, 4);
      const payload = { source: 'admin', title: form.title.trim().toUpperCase(), subtitle: form.subtitle.trim(), description: form.description.trim(), price: form.price.trim(), tag: form.tag.trim(), category: form.category, image: form.image.trim(), notes, optionGroupIds: form.optionGroupIds, actif: !!form.actif, deleted: false, ordre: editing?.ordre ?? produitsList.length + 1, updatedAt: serverTimestamp() };
      await setDoc(doc(db, 'produits', id), payload, { merge: true });
      if (payload.image) await setDoc(doc(db, 'images', id), { image: payload.image }, { merge: true });
      setEditing(null); setForm(emptyProduct); flash('✅ Produit enregistré.');
    } catch (e) { alert('Erreur: ' + e.message); }
    finally { setBusy(false); }
  };

  const deleteProduit = async (p) => {
    if (!window.confirm(`Supprimer "${p.title}" ?`)) return;
    try { await setDoc(doc(db, 'produits', p.id), { deleted: true, actif: false, updatedAt: serverTimestamp() }, { merge: true }); flash('Produit supprimé.'); }
    catch (e) { alert('Erreur: ' + e.message); }
  };

  const toggleProduit = async (p) => {
    try { await setDoc(doc(db, 'produits', p.id), { actif: p.actif === false, updatedAt: serverTimestamp() }, { merge: true }); }
    catch (e) { alert('Erreur: ' + e.message); }
  };

  /* OPTION GROUP SAVE */
  const saveGroup = async () => {
    if (!groupForm.title.trim()) return flash('Titre obligatoire.');
    setBusy(true);
    try {
      const id = groupForm.id || slug(groupForm.title) || `g-${Date.now()}`;
      const items = groupForm.items.map(it => ({ id: it.id || slug(it.label || ''), label: it.label.trim(), description: (it.description || '').trim(), price: Number(it.price || 0), image: (it.image || '').trim(), hidden: !!it.hidden })).filter(it => it.id && it.label);
      if (items.length === 0) return flash('Ajoute au moins un choix.');
      await setDoc(doc(db, 'optionGroups', id), { title: groupForm.title.trim(), subtitle: groupForm.subtitle.trim(), min: Number(groupForm.min || 0), max: Number(groupForm.max || 1), columns: Number(groupForm.columns || 2), compact: Number(groupForm.columns || 2) >= 3, sauce: id.includes('sauce'), menu: id === 'formule', items, actif: !!groupForm.actif, updatedAt: serverTimestamp() }, { merge: true });
      setEditingGroup(null); setGroupForm(emptyGroup); flash('✅ Groupe enregistré.');
    } catch (e) { alert('Erreur: ' + e.message); }
    finally { setBusy(false); }
  };

  /* CATEGORY SAVE */
  const saveCat = async () => {
    if (!catForm.name.trim()) return flash('Nom obligatoire.');
    try {
      const id = slug(catForm.name) || `c-${Date.now()}`;
      await setDoc(doc(db, 'categories', id), { name: catForm.name.trim(), icon: catForm.icon.trim() || '🍽', actif: true, ordre: catList.length + 1, updatedAt: serverTimestamp() }, { merge: true });
      setCatForm({ name: '', icon: '🍽' }); flash('✅ Catégorie enregistrée.');
    } catch (e) { alert('Erreur: ' + e.message); }
  };

  /* SETTINGS SAVE */
  const saveSettings = async () => {
    try { await setDoc(doc(db, 'settings', 'general'), { ...settings, updatedAt: serverTimestamp() }, { merge: true }); flash('✅ Paramètres enregistrés.'); }
    catch (e) { alert('Erreur: ' + e.message); }
  };

  /* ITEM (sub-product) SAVE */
  const saveItem = async () => {
    if (!itemForm.label.trim()) return flash('Nom obligatoire.');
    setBusy(true);
    try {
      const srcGroupId = editingItem.groupId;
      const idx = editingItem.itemIndex;
      const original = (optionGroups[srcGroupId]?.items || [])[idx] || {};
      const updatedItem = {
        id: original.id,
        label: itemForm.label.trim(),
        price: Number(itemForm.price || 0),
        description: (itemForm.description || '').trim(),
        image: (itemForm.image || '').trim(),
      };
      const targetGroupId = itemForm.groupId;
      if (targetGroupId === srcGroupId) {
        const items = (optionGroups[srcGroupId]?.items || []).map((it, i) => i === idx ? updatedItem : it);
        await setDoc(doc(db, 'optionGroups', srcGroupId), { items, updatedAt: serverTimestamp() }, { merge: true });
      } else {
        const srcItems = (optionGroups[srcGroupId]?.items || []).filter((_, i) => i !== idx);
        const destItems = [...(optionGroups[targetGroupId]?.items || []), updatedItem];
        await Promise.all([
          setDoc(doc(db, 'optionGroups', srcGroupId), { items: srcItems, updatedAt: serverTimestamp() }, { merge: true }),
          setDoc(doc(db, 'optionGroups', targetGroupId), { items: destItems, updatedAt: serverTimestamp() }, { merge: true }),
        ]);
      }
      setEditingItem(null);
      flash('✅ Produit enregistré !');
    } catch (e) { alert(e.message); }
    finally { setBusy(false); }
  };

  /* SIPARIS STATUS */
  const updateSiparis = async (id, statut) => {
    try { await setDoc(doc(db, 'siparisler', id), { statut, updatedAt: serverTimestamp() }, { merge: true }); }
    catch (e) { alert('Erreur: ' + e.message); }
  };

  /* DELETE OLD ORDERS */
  const deleteOldOrders = async () => {
    const pin = window.prompt('Entrez le code PIN pour supprimer les anciennes commandes :');
    if (pin === null) return;
    if (pin !== '2109') { alert('❌ PIN incorrect.'); return; }
    const toDelete = siparisler.filter(s => s.statut !== 'nouveau');
    if (toDelete.length === 0) { alert('Aucune commande traitée à supprimer.'); return; }
    if (!window.confirm(`Supprimer ${toDelete.length} commande(s) acceptée(s) / terminée(s) / annulée(s) ?`)) return;
    try {
      setBusy(true);
      const { writeBatch, doc: fbDoc } = await import('firebase/firestore');
      let batch = writeBatch(db);
      let count = 0;
      for (const s of toDelete) {
        batch.delete(fbDoc(db, 'siparisler', s.id));
        count++;
        if (count % 499 === 0) { await batch.commit(); batch = writeBatch(db); count = 0; }
      }
      await batch.commit();
      flash(`✅ ${toDelete.length} commande(s) supprimée(s).`);
    } catch (e) { alert('Erreur: ' + e.message); }
    finally { setBusy(false); }
  };

  /* TOGGLE VISIBILITY */
  const toggleCatHidden = async (p) => {
    try { await setDoc(doc(db, 'produits', p.id), { hidden: !p.hidden, updatedAt: serverTimestamp() }, { merge: true }); }
    catch (e) { alert(e.message); }
  };

  const toggleItemHidden = async (groupId, itemId) => {
    const group = optionGroups[groupId];
    if (!group) return;
    const newItems = (group.items || []).map(it => it.id === itemId ? { ...it, hidden: !it.hidden } : it);
    try { await setDoc(doc(db, 'optionGroups', groupId), { items: newItems }, { merge: true }); }
    catch (e) { alert(e.message); }
  };

  /* LOGIN */
  const doLogin = async () => {
    if (!pwInput.trim()) return;
    setLoginLoading(true);
    try {
      await signInWithEmailAndPassword(firebaseAuth, emailInput, pwInput);
    } catch (e) {
      flash('❌ Email ou mot de passe incorrect.');
    } finally {
      setLoginLoading(false);
    }
  };

  if (!auth) {
    return (
      <div className="admin-login">
        <div className="admin-login-box">
          <div className="admin-login-logo">🍽 BISCORNUE</div>
          <h2>Admin</h2>
          <input type="email" value={emailInput} onChange={e => setEmailInput(e.target.value)} placeholder="Email" style={{marginBottom: 8}} />
          <input type="password" value={pwInput} onChange={e => setPwInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && doLogin()}
            placeholder="Mot de passe" autoFocus />
          {msg && <p className="admin-login-error">{msg}</p>}
          <button onClick={doLogin} disabled={loginLoading}>
            {loginLoading ? '⏳ Connexion...' : 'Entrer'}
          </button>
        </div>
      </div>
    );
  }

  const NAV_ITEMS = [
    ['produits', '🍕', 'Produits'],
    ['options', '🧩', 'Options'],
    ['commandes', '📋', 'Commandes'],
    ['stats', '📊', 'Statistiques'],
    ['settings', '⚙️', 'Paramètres'],
  ];

  return (
    <div className="admin-page">
      <aside className="admin-sidebar">
        <div className="admin-sidebar-logo">🍽 <span>BISCORNUE</span></div>
        <nav>
          {NAV_ITEMS.map(([k, icon, label]) => (
            <button key={k} className={section === k ? 'active' : ''} onClick={() => setSection(k)}>{icon} {label}</button>
          ))}
          <a href="/" target="_blank" rel="noreferrer">🌐 Voir le site</a>
          <button onClick={() => signOut(firebaseAuth)} style={{marginTop:'auto',opacity:0.6,fontSize:12}}>🔓 Déconnexion</button>
        </nav>
      </aside>

      {/* MOBILE BOTTOM NAV */}
      <nav className="admin-mobile-nav">
        {NAV_ITEMS.map(([k, icon, label]) => (
          <button key={k} className={`admin-mobile-nav-item ${section === k ? 'active' : ''}`} onClick={() => setSection(k)}>
            <span>{icon}</span>
            <span>{label}</span>
            {k === 'commandes' && siparisler.filter(s => s.statut === 'nouveau').length > 0 && (
              <span className="admin-mobile-badge">{siparisler.filter(s => s.statut === 'nouveau').length}</span>
            )}
          </button>
        ))}
      </nav>

      <main className="admin-main">
        {msg && <div className="admin-msg">{msg}</div>}

        {/* ── CATÉGORIES (main products) ── */}
        {section === 'produits' && (
          <>
            <div className="admin-topbar">
              <div><h1>Produits</h1><p>Les pizzas de la carte : photo, prix, description, visibilité</p></div>
              <div style={{ display: 'flex', gap: 10 }}>
                {produitsList.length === 0 && <button className="admin-btn-seed" onClick={seed} disabled={busy}>🌱 Initialiser le menu</button>}
                <button className="admin-btn-seed" onClick={seed} disabled={busy}>🔄 Réinitialiser</button>
              </div>
            </div>
            <div className="admin-cat-blocks">
              {produitsList.map(p => {
                const img = p.image || images[p.id] || '';
                const isEditingThis = editing?.id === p.id;
                return (
                  <div key={p.id} className={`admin-cat-block ${p.hidden ? 'admin-hidden' : ''}`}>
                    {/* Image */}
                    <div className="admin-cat-block-img">
                      {img ? <img src={img} alt={p.title} /> : <span>🍽</span>}
                      <label className="admin-cat-block-upload">
                        {busy ? '⏳' : '📷 Changer la photo'}
                        <input type="file" accept="image/*" onChange={async e => {
                          if (!e.target.files[0]) return;
                          try {
                            const url = await upload(e.target.files[0]);
                            await setDoc(doc(db, 'produits', p.id), { image: url, updatedAt: serverTimestamp() }, { merge: true });
                            await setDoc(doc(db, 'images', p.id), { image: url }, { merge: true });
                            flash('✅ Photo mise à jour !');
                          } catch (err) { alert(err.message); }
                        }} />
                      </label>
                      {img && (
                        <button
                          className="admin-cat-block-del"
                          title="Supprimer la photo"
                          onClick={async () => {
                            try {
                              await setDoc(doc(db, 'produits', p.id), { image: '', updatedAt: serverTimestamp() }, { merge: true });
                              await setDoc(doc(db, 'images', p.id), { image: '' }, { merge: true });
                              flash('🗑 Photo supprimée');
                            } catch (err) { alert(err.message); }
                          }}
                        >🗑</button>
                      )}
                    </div>
                    {/* Info */}
                    <div className="admin-cat-block-info">
                      {isEditingThis ? (
                        <>
                          <input className="admin-cat-inline-input" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Nom de la catégorie" />
                          <input className="admin-cat-inline-input small" value={form.displayPrice} onChange={e => setForm(f => ({ ...f, displayPrice: e.target.value }))} placeholder="Prix affiché, ex: À partir de €10.00" />
                          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                            <button className="admin-btn-primary small" onClick={saveProduit} disabled={busy}>Enregistrer</button>
                            <button className="admin-btn-ghost small" onClick={() => { setEditing(null); setForm(emptyProduct); }}>Annuler</button>
                          </div>
                        </>
                      ) : (
                        <>
                          <strong>{p.title}</strong>
                          <span>{p.displayPrice}</span>
                          <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                            <button className="admin-btn-ghost small" onClick={() => { setEditing(p); setForm({ ...emptyProduct, ...p, notesText: (p.notes || []).join('\n'), optionGroupIds: p.optionGroupIds || [] }); }}>✏️ Modifier</button>
                            <button className={`admin-visibility-btn ${p.hidden ? 'hidden' : ''}`} onClick={() => toggleCatHidden(p)} title={p.hidden ? 'Afficher' : 'Masquer'}>
                              {p.hidden ? '🚫 Masqué' : '👁 Visible'}
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
              {produitsList.length === 0 && (
                <div className="admin-empty"><p>Clique sur "Initialiser le menu" pour commencer.</p></div>
              )}
            </div>
          </>
        )}

        {/* ── PRODUITS (sub-items) ── */}
        {section === 'items' && (() => {
          const allItems = TYPE_GROUP_IDS.flatMap(gid => {
            const group = optionGroups[gid];
            if (!group?.items) return [];
            return group.items.map((item, itemIndex) => ({ ...item, groupId: gid, itemIndex }));
          });
          return (
            <>
              <div className="admin-topbar">
                <div><h1>Produits</h1><p>Modifier les produits, leur photo, prix et contenu</p></div>
              </div>
              <div className="admin-items-layout">
                {/* Liste */}
                <div className="admin-items-list-panel">
                  {TYPE_GROUP_IDS.map(gid => {
                    const group = optionGroups[gid];
                    if (!group?.items?.length) return null;
                    return (
                      <div key={gid} className="admin-items-group">
                        <div className="admin-items-group-title">{CATEGORY_LABELS[gid]}</div>
                        {group.items.map((item, itemIndex) => {
                          const isActive = editingItem?.groupId === gid && editingItem?.itemIndex === itemIndex;
                          return (
                            <div
                              key={item.id}
                              className={`admin-items-row ${isActive ? 'active' : ''} ${item.hidden ? 'admin-row-hidden' : ''}`}
                              onClick={() => {
                                setEditingItem({ groupId: gid, itemIndex });
                                setItemForm({ label: item.label, price: item.price ?? 0, description: item.description || '', image: item.image || '', groupId: gid });
                              }}
                            >
                              <div className="admin-items-thumb">
                                {item.image ? <img src={item.image} alt={item.label} /> : <span>🍽</span>}
                              </div>
                              <div style={{ flex: 1 }}>
                                <strong>{item.label}</strong>
                                <span>€{Number(item.price || 0).toFixed(2)}</span>
                              </div>
                              <button
                                className={`admin-eye-btn ${item.hidden ? 'off' : ''}`}
                                title={item.hidden ? 'Afficher' : 'Masquer'}
                                onClick={e => { e.stopPropagation(); toggleItemHidden(gid, item.id); }}
                              >{item.hidden ? '🚫' : '👁'}</button>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>

                {/* Éditeur */}
                {editingItem && (
                  <div className="admin-items-editor">
                    <h3>Modifier : {itemForm.label || '—'}</h3>

                    {/* Photo */}
                    <div className="admin-items-editor-img">
                      {itemForm.image
                        ? <img src={itemForm.image} alt={itemForm.label} />
                        : <span>🍽</span>
                      }
                      <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                        <label className="admin-photo-btn">
                          {busy ? '⏳ Upload...' : '📷 Changer la photo'}
                          <input type="file" accept="image/*" onChange={async e => {
                            if (!e.target.files[0]) return;
                            try {
                              const url = await upload(e.target.files[0]);
                              setItemForm(f => ({ ...f, image: url }));
                            } catch (err) { alert(err.message); }
                          }} />
                        </label>
                        {itemForm.image && (
                          <button
                            type="button"
                            className="admin-photo-del-btn"
                            onClick={() => setItemForm(f => ({ ...f, image: '' }))}
                            title="Supprimer la photo"
                          >🗑 Supprimer</button>
                        )}
                      </div>
                    </div>

                    <label className="admin-field-label">Nom du produit
                      <input value={itemForm.label} onChange={e => setItemForm(f => ({ ...f, label: e.target.value }))} placeholder="Ex : Kebab" />
                    </label>

                    <label className="admin-field-label">Prix (€)
                      <input type="number" step="0.1" min="0" value={itemForm.price} onChange={e => setItemForm(f => ({ ...f, price: e.target.value }))} />
                    </label>

                    <label className="admin-field-label">Contenu / Description
                      <textarea rows={5} value={itemForm.description} onChange={e => setItemForm(f => ({ ...f, description: e.target.value }))} placeholder="Ingrédients, sauces, suppléments..." />
                    </label>

                    <label className="admin-field-label">Catégorie
                      <select value={itemForm.groupId} onChange={e => setItemForm(f => ({ ...f, groupId: e.target.value }))}>
                        {TYPE_GROUP_IDS.map(gid => (
                          <option key={gid} value={gid}>{CATEGORY_LABELS[gid]}</option>
                        ))}
                      </select>
                    </label>

                    <div className="admin-items-editor-actions">
                      <button className="admin-btn-primary" onClick={saveItem} disabled={busy}>
                        {busy ? 'Enregistrement...' : '✅ Enregistrer'}
                      </button>
                      <button className="admin-btn-ghost" onClick={() => setEditingItem(null)}>Annuler</button>
                    </div>
                  </div>
                )}
              </div>
            </>
          );
        })()}

        {/* ── PHOTOS ── */}
        {section === 'photos' && (
          <>
            <div className="admin-topbar">
              <div><h1>Photos</h1><p>Images des catégories et de leurs produits</p></div>
            </div>
            <div className="admin-photos-page">
              {produitsList.map(p => {
                const firstGroupId = (p.optionGroupIds || [])[0];
                const group = firstGroupId ? optionGroups[firstGroupId] : null;
                const items = group?.items || [];
                const mainImg = p.image || images[p.id] || '';
                return (
                  <div key={p.id} className="admin-photo-block">
                    {/* Catégorie principale */}
                    <div className="admin-photo-row main-row">
                      <div className="admin-photo-thumb large">
                        {mainImg ? <img src={mainImg} alt={p.title} /> : <span>🍽</span>}
                      </div>
                      <div className="admin-photo-meta">
                        <strong>{p.title}</strong>
                        <span>Photo de la catégorie</span>
                      </div>
                      <label className="admin-photo-btn">
                        {busy ? '⏳' : '📷 Changer'}
                        <input type="file" accept="image/*" onChange={async e => {
                          if (!e.target.files[0]) return;
                          try {
                            const url = await upload(e.target.files[0]);
                            await setDoc(doc(db, 'produits', p.id), { image: url, updatedAt: serverTimestamp() }, { merge: true });
                            await setDoc(doc(db, 'images', p.id), { image: url }, { merge: true });
                            flash('✅ Photo mise à jour !');
                          } catch (err) { alert(err.message); }
                        }} />
                      </label>
                    </div>

                    {/* Sous-produits */}
                    {items.length > 0 && (
                      <div className="admin-photo-subitems">
                        {items.map((item, i) => (
                          <div key={item.id} className="admin-photo-row sub-row">
                            <div className="admin-photo-thumb small">
                              {item.image ? <img src={item.image} alt={item.label} /> : <span>🍽</span>}
                            </div>
                            <div className="admin-photo-meta">
                              <span>{item.label}</span>
                              {item.price > 0 && <small>€{Number(item.price).toFixed(2)}</small>}
                            </div>
                            <label className="admin-photo-btn small">
                              {busy ? '⏳' : '📷'}
                              <input type="file" accept="image/*" onChange={async e => {
                                if (!e.target.files[0]) return;
                                try {
                                  const url = await upload(e.target.files[0]);
                                  const updatedItems = items.map((it, j) => j === i ? { ...it, image: url } : it);
                                  await setDoc(doc(db, 'optionGroups', firstGroupId), { items: updatedItems, updatedAt: serverTimestamp() }, { merge: true });
                                  flash(`✅ Photo "${item.label}" mise à jour !`);
                                } catch (err) { alert(err.message); }
                              }} />
                            </label>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* ── OPTIONS ── */}
        {section === 'options' && (
          <>
            <div className="admin-topbar">
              <div><h1>Groupes d'options</h1><p>Attachés aux produits lors de la commande</p></div>
              <button className="admin-btn-primary" onClick={() => { setEditingGroup({}); setGroupForm(emptyGroup); }}>+ Nouveau groupe</button>
            </div>
            <div className="admin-option-grid">
              {groupList.map(g => (
                <div key={g.id} className={`admin-option-card ${g.actif === false ? 'inactive' : ''} ${editingGroup?.id === g.id ? 'active' : ''}`}>
                  <div className="admin-option-card-head">
                    <div>
                      <strong>{g.title}</strong>
                      <span>{g.subtitle || `min ${g.min} / max ${g.max}`}</span>
                    </div>
                    <button className="admin-option-card-btn" onClick={() => {
                      if (editingGroup?.id === g.id) { setEditingGroup(null); setGroupForm(emptyGroup); }
                      else { setEditingGroup(g); setGroupForm({ ...emptyGroup, ...g, items: (g.items || []).map(it => ({ ...it })) }); }
                    }}>{editingGroup?.id === g.id ? '✕ Fermer' : '✏️ Modifier'}</button>
                  </div>
                  <div className="admin-option-chips">
                    {(g.items || []).map(it => (
                      <span key={it.id} className={`admin-option-chip ${it.hidden ? 'chip-hidden' : ''} ${it.image ? '' : 'chip-no-img'}`}>
                        {it.image
                          ? <img src={it.image} alt="" className="chip-thumb" />
                          : <span className="chip-no-img-icon">📷</span>
                        }
                        {it.hidden ? '🚫 ' : ''}{it.label}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {editingGroup !== null && (
              <div className="admin-option-editor-full">
                  <h3>{editingGroup.id ? `✏️ Modifier : ${editingGroup.title}` : '+ Nouveau groupe'}</h3>
                  <div className="admin-form-row">
                    <label>Titre<input value={groupForm.title} onChange={e => setGroupForm(p => ({ ...p, title: e.target.value }))} placeholder="SAUCE" /></label>
                    <label>Sous-titre<input value={groupForm.subtitle} onChange={e => setGroupForm(p => ({ ...p, subtitle: e.target.value }))} placeholder="Choisir entre 1 et 2" /></label>
                    <label>Min<input type="number" min="0" value={groupForm.min} onChange={e => setGroupForm(p => ({ ...p, min: e.target.value }))} /></label>
                    <label>Max<input type="number" min="1" value={groupForm.max} onChange={e => setGroupForm(p => ({ ...p, max: e.target.value }))} /></label>
                    <label>Colonnes
                      <select value={groupForm.columns} onChange={e => setGroupForm(p => ({ ...p, columns: e.target.value }))}>
                        <option value="2">2</option><option value="3">3</option><option value="4">4</option>
                      </select>
                    </label>
                  </div>
                  <div className="admin-items-list">
                    {groupForm.items.map((it, i) => (
                      <div className={`admin-item-row ${it.hidden ? 'admin-row-hidden' : ''}`} key={i}>
                        {/* Visibilité */}
                        <button
                          className={`admin-eye-btn ${it.hidden ? 'off' : ''}`}
                          title={it.hidden ? 'Afficher' : 'Masquer'}
                          onClick={() => setGroupForm(p => ({ ...p, items: p.items.map((x, j) => j === i ? { ...x, hidden: !x.hidden } : x) }))}
                        >{it.hidden ? '🚫' : '👁'}</button>
                        {/* Photo */}
                        <div className="admin-item-photo-cell">
                          {it.image
                            ? <>
                                <img src={it.image} alt="" className="admin-item-thumb" />
                                <button
                                  className="admin-item-photo-del"
                                  title="Supprimer la photo"
                                  onClick={() => setGroupForm(p => ({ ...p, items: p.items.map((x, j) => j === i ? { ...x, image: '' } : x) }))}
                                >✕</button>
                              </>
                            : <label className="admin-item-photo-add" title="Ajouter une photo">
                                📷
                                <input type="file" accept="image/*" onChange={async e => {
                                  if (!e.target.files[0]) return;
                                  const url = await upload(e.target.files[0]);
                                  setGroupForm(p => ({ ...p, items: p.items.map((x, j) => j === i ? { ...x, image: url } : x) }));
                                }} />
                              </label>
                          }
                        </div>
                        <input value={it.label} onChange={e => setGroupForm(p => ({ ...p, items: p.items.map((x, j) => j === i ? { ...x, label: e.target.value } : x) }))} placeholder="Nom, ex: Ketchup" />
                        <input value={it.id} onChange={e => setGroupForm(p => ({ ...p, items: p.items.map((x, j) => j === i ? { ...x, id: slug(e.target.value) } : x) }))} placeholder="ID" style={{ width: 90 }} />
                        <input type="number" step="0.1" value={it.price} onChange={e => setGroupForm(p => ({ ...p, items: p.items.map((x, j) => j === i ? { ...x, price: e.target.value } : x) }))} placeholder="Prix" style={{ width: 70 }} />
                        <button className="admin-item-del" onClick={() => setGroupForm(p => ({ ...p, items: p.items.filter((_, j) => j !== i) }))}>🗑</button>
                      </div>
                    ))}
                  </div>
                  <div className="admin-form-actions">
                    <button onClick={() => setGroupForm(p => ({ ...p, items: [...p.items, { id: '', label: '', price: 0, image: '' }] }))}>+ Ajouter un choix</button>
                    <button className="admin-btn-primary" onClick={saveGroup} disabled={busy}>Enregistrer</button>
                    <button onClick={() => { setEditingGroup(null); setGroupForm(emptyGroup); }}>Annuler</button>
                  </div>
                </div>
              )}
          </>
        )}

        {/* ── CATÉGORIES ── */}
        {section === 'categories' && (
          <>
            <div className="admin-topbar"><div><h1>Catégories</h1><p>Filtres affichés sur le site</p></div></div>
            <div className="admin-cat-form">
              <input value={catForm.name} onChange={e => setCatForm(p => ({ ...p, name: e.target.value }))} placeholder="Nom, ex: Assiettes" />
              <input value={catForm.icon} onChange={e => setCatForm(p => ({ ...p, icon: e.target.value }))} placeholder="🍽" maxLength={4} />
              <button className="admin-btn-primary" onClick={saveCat}>Ajouter</button>
            </div>
            <div className="admin-cat-list">
              {catList.map(c => (
                <div key={c.id} className={`admin-cat-item ${c.actif === false ? 'inactive' : ''}`}>
                  <span>{c.icon}</span> {c.name}
                  <button onClick={async () => { await setDoc(doc(db, 'categories', c.id), { actif: c.actif === false, updatedAt: serverTimestamp() }, { merge: true }); }}>
                    {c.actif === false ? 'Afficher' : 'Masquer'}
                  </button>
                </div>
              ))}
            </div>
          </>
        )}

        {section === 'stats' && <StatsSection siparisler={siparisler} />}

        {/* ── COMMANDES ── */}
        {section === 'commandes' && (
          <>
            <div className="admin-topbar">
              <div><h1>Commandes</h1><p>{siparisler.filter(s => s.statut === 'nouveau').length} nouvelle{siparisler.filter(s => s.statut === 'nouveau').length !== 1 ? 's' : ''}</p></div>
              <button onClick={deleteOldOrders} style={{background:'#ef4444',color:'#fff',border:'none',borderRadius:'8px',padding:'8px 16px',cursor:'pointer',fontWeight:'bold',fontSize:'13px'}}>🗑️ Supprimer les anciennes</button>
            </div>
            <div className="admin-orders">
              {siparisler.length === 0 && <div className="admin-empty"><p>Aucune commande pour le moment.</p></div>}
              {joursCommandes.map(([cle, liste]) => (
                <div key={cle}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '18px 0 10px' }}>
                    <strong style={{ fontSize: 15, textTransform: 'capitalize' }}>📅 {labelJour(liste[0].jourDate)}</strong>
                    <span style={{ fontSize: 13, color: '#6B7280' }}>{liste.length} commande{liste.length > 1 ? 's' : ''}</span>
                    <span style={{ flex: 1, height: 1, background: '#E5E7EB' }} />
                  </div>
                  {liste.map(s => (
                <div key={s.id} className={`admin-order ${s.statut}`}>
                  <div className="admin-order-head">
                    <strong>{s.nom}</strong>
                    <span>{labelJour(s.jourDate)} · retrait à {s.heure}</span>
                    <em className={`status-${s.statut}`}>{s.statut}</em>
                  </div>
                  <div className="admin-order-items">
                    {(s.items || []).map((it, i) => <div key={i}><span>{it.label}</span><small>{it.resume}</small><b>{money(it.prixTotal)}</b></div>)}
                  </div>
                  <div className="admin-order-total">Total : <strong>{money(s.total || 0)}</strong></div>
                  <div className="admin-order-actions">
                    {s.statut === 'nouveau' && <button className="btn-accept" onClick={() => updateSiparis(s.id, 'accepté')}>✅ Accepter</button>}
                    {s.statut === 'accepté' && <button className="btn-done" onClick={() => updateSiparis(s.id, 'prêt')}>🔔 Prêt</button>}
                    {s.statut !== 'annulé' && <button className="btn-cancel" onClick={() => updateSiparis(s.id, 'annulé')}>✕ Annuler</button>}
                  </div>
                </div>
                  ))}
                </div>
              ))}
            </div>
          </>
        )}

        {/* ── PARAMÈTRES ── */}
        {section === 'settings' && (
          <>
            <div className="admin-topbar"><div><h1>Paramètres</h1><p>Horaires et configuration</p></div></div>
            <div className="admin-settings">
              <label className="admin-toggle-row">
                <span>Restoran fermé (forcer la fermeture)</span>
                <input type="checkbox" checked={!!settings.ferme} onChange={e => setSettings(p => ({ ...p, ferme: e.target.checked }))} />
              </label>
              {settings.ferme && (
                <label style={{display:'block',marginTop:8}}>
                  <span style={{fontSize:13,color:'#6B7280',display:'block',marginBottom:6}}>
                    💬 Message à afficher aux clients (visible sur le site)
                  </span>
                  <textarea
                    rows={4}
                    placeholder="Ex : Nous sommes fermés aujourd'hui pour cause de congés. À bientôt ! 🌿"
                    value={settings.fermeMessage || ''}
                    onChange={e => setSettings(p => ({ ...p, fermeMessage: e.target.value }))}
                    style={{width:'100%',padding:'10px 12px',borderRadius:8,border:'1.5px solid #E5E7EB',
                      fontSize:14,resize:'vertical',fontFamily:'inherit',boxSizing:'border-box'}}
                  />
                </label>
              )}
              <label>Délai de préparation (minutes)
                <input type="number" min="5" step="5" value={settings.delai} onChange={e => setSettings(p => ({ ...p, delai: Number(e.target.value) }))} />
              </label>
              <label>Pas des créneaux au Ligeria (minutes)
                <input type="number" min="5" step="5" value={settings.step ?? 10} onChange={e => setSettings(p => ({ ...p, step: Number(e.target.value) }))} />
              </label>
              <label>Navette vers le Tabac Oudon : toutes les … minutes
                <input type="number" min="5" step="5" value={settings.navInt ?? 30} onChange={e => setSettings(p => ({ ...p, navInt: Number(e.target.value) }))} />
              </label>
              <label>Tabac Oudon : dernier retrait (plus aucune commande Tabac après, ni pour le lendemain)
                <input type="time" value={settings.tabacDernier || '21:00'} onChange={e => setSettings(p => ({ ...p, tabacDernier: e.target.value }))} />
              </label>
              <label>Lien Google « laisser un avis » (QR code imprimé sur le menu et la carte de visite)
                <input type="url" placeholder="https://g.page/r/…/review" value={settings.googleAvisUrl || ''} onChange={e => setSettings(p => ({ ...p, googleAvisUrl: e.target.value.trim() }))} />
              </label>
              <div className="admin-services">
                <strong>Horaires d'ouverture</strong>
                {(settings.services || []).map((s, i) => (
                  <div key={i} className="admin-service-row">
                    <label>Ouverture<input type="text" inputMode="numeric" placeholder="09:00" maxLength={5} value={s.ouverture} onChange={e => setSettings(p => ({ ...p, services: p.services.map((x, j) => j === i ? { ...x, ouverture: fmtTime(e.target.value) } : x) }))} /></label>
                    <label>Fermeture<input type="text" inputMode="numeric" placeholder="22:00" maxLength={5} value={s.fermeture} onChange={e => setSettings(p => ({ ...p, services: p.services.map((x, j) => j === i ? { ...x, fermeture: fmtTime(e.target.value) } : x) }))} /></label>
                    <label className="admin-toggle-small">
                      <input type="checkbox" checked={s.actif !== false} onChange={e => setSettings(p => ({ ...p, services: p.services.map((x, j) => j === i ? { ...x, actif: e.target.checked } : x) }))} />
                      Actif
                    </label>
                    <button onClick={() => setSettings(p => ({ ...p, services: p.services.filter((_, j) => j !== i) }))}>✕</button>
                  </div>
                ))}
                <button onClick={() => setSettings(p => ({ ...p, services: [...(p.services || []), { ouverture: '11:00', fermeture: '22:00', actif: true }] }))}>+ Ajouter un service</button>
              </div>
              <button className="admin-btn-primary" onClick={saveSettings}>Enregistrer les paramètres</button>
              {/* ── THÈME ── */}
              <div style={{ marginTop: 32, paddingTop: 24, borderTop: '1px solid #E5E7EB' }}>
                <strong style={{ display: 'block', marginBottom: 4 }}>🎨 Thème de couleurs</strong>
                <p style={{ color: '#6B7280', fontSize: 13, marginBottom: 16 }}>Personnalise les couleurs affichées sur le site client.</p>
                {[
                  { key: 'mono',  label: '⬤  Mono' },
                  { key: 'multi', label: '🌈 Multi' },
                  { key: 'neon',  label: '💡 Néon' },
                ].map(({ key, label }) => (
                  <div key={key} style={{ marginBottom: 18 }}>
                    <div style={{ fontSize: 11, fontWeight: 900, color: '#6B7280', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>{label}</div>
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                      {THEMES.filter(t => t.mode === key).map(t => {
                        const active = (settings.themeId || 'green') === t.id;
                        return (
                          <button
                            key={t.id}
                            title={t.name}
                            onClick={() => {
                              setSettings(p => ({ ...p, themeId: t.id }));
                              applyTheme(t.id);
                            }}
                            style={{
                              width: 44,
                              height: 44,
                              borderRadius: 10,
                              border: active ? '3px solid #111' : '2px solid transparent',
                              background: t.gradient || t.primary,
                              cursor: 'pointer',
                              outline: active ? '2px solid #fff' : 'none',
                              outlineOffset: -4,
                              boxShadow: t.mode === 'neon'
                                ? `0 0 14px ${t.primary}88`
                                : '0 2px 8px rgba(0,0,0,0.18)',
                              position: 'relative',
                              flexShrink: 0,
                            }}
                          >
                            {active && (
                              <span style={{
                                position: 'absolute', inset: 0, display: 'flex',
                                alignItems: 'center', justifyContent: 'center',
                                fontSize: 18, color: '#fff',
                                textShadow: '0 1px 3px rgba(0,0,0,0.5)',
                              }}>✓</span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ marginTop: 32, paddingTop: 24, borderTop: '1px solid #E5E7EB' }}>
                <strong style={{ display: 'block', marginBottom: 8 }}>Initialisation</strong>
                <p style={{ color: '#6B7280', fontSize: 13, marginBottom: 12 }}>Remettre les 7 produits, catégories et options de base (sans écraser).</p>
                <button className="admin-btn-seed" onClick={seed} disabled={busy}>🌱 Initialiser le menu de base</button>
              </div>
            </div>
          </>
        )}
      </main>

      {/* MODAL PRODUIT */}
      {editing !== null && (
        <div className="admin-modal-overlay" onClick={e => e.target.className === 'admin-modal-overlay' && setEditing(null)}>
          <div className="admin-modal">
            <div className="admin-modal-head">
              <h2>{editing.id ? 'Modifier le produit' : 'Nouveau produit'}</h2>
              <button onClick={() => setEditing(null)}>×</button>
            </div>
            <div className="admin-modal-body">
              <div className="admin-form-2col">
                <label>Nom *<input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} /></label>
                <label>Prix<input value={form.price} onChange={e => setForm(p => ({ ...p, price: e.target.value }))} /></label>
                <label>Sous-titre<input value={form.subtitle} onChange={e => setForm(p => ({ ...p, subtitle: e.target.value }))} /></label>
                <label>Catégorie
                  <select value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))}>
                    <option value="">— choisir —</option>
                    {catList.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                  </select>
                </label>
                <label>Tag<input value={form.tag} onChange={e => setForm(p => ({ ...p, tag: e.target.value }))} /></label>
                <label className="admin-toggle-small">
                  <input type="checkbox" checked={form.actif} onChange={e => setForm(p => ({ ...p, actif: e.target.checked }))} />
                  Visible sur le site
                </label>
                <label className="full">Description<textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} /></label>
                <label className="full">Notes (une par ligne)<textarea value={form.notesText} onChange={e => setForm(p => ({ ...p, notesText: e.target.value }))} /></label>
                <div className="full admin-option-check">
                  <strong>Groupes d'options</strong>
                  {groupList.map(g => (
                    <label key={g.id}>
                      <input type="checkbox" checked={form.optionGroupIds.includes(g.id)} onChange={e => setForm(p => ({ ...p, optionGroupIds: e.target.checked ? [...p.optionGroupIds, g.id] : p.optionGroupIds.filter(x => x !== g.id) }))} />
                      {g.title} <span>min {g.min} / max {g.max}</span>
                    </label>
                  ))}
                </div>
                <label className="full">URL photo<input value={form.image} onChange={e => setForm(p => ({ ...p, image: e.target.value }))} /></label>
                <label className="full admin-file-label">
                  Téléverser une photo
                  <input type="file" accept="image/*" onChange={async e => { const url = await upload(e.target.files[0]); setForm(p => ({ ...p, image: url })); }} />
                  {busy && <span>Upload...</span>}
                </label>
                {form.image && <img className="admin-preview" src={form.image} alt="" />}
              </div>
            </div>
            <div className="admin-modal-foot">
              <button onClick={() => setEditing(null)}>Annuler</button>
              <button className="admin-btn-primary" onClick={saveProduit} disabled={busy}>{busy ? '...' : 'Enregistrer'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Admin;
